import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { BuscaAnimal } from '../components/BuscaAnimal.js';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  classeTd,
  classeTh,
  Etiqueta,
  EstadoVazio,
  Indicador,
  Painel,
  Tabela,
} from '../components/ui/index.js';
import { classeNaData, formatarData, idadeDoAnimal, ROTULO_CLASSE } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Avaliacao } from '../dados/fonte.js';
import { arquivos, db } from '../lib/local.js';
import { minutosDaFicha, proximaFicha } from './Coleta.js';
import { ROTULO_SITUACAO, situacaoDaVisita } from './Visitas.js';

/** Situacao de uma ficha, para a lista da visita. */
function EtiquetaDaFicha({ a }: { a: Avaliacao }) {
  if (a.status === 'validada') return <Etiqueta tom="ok">Validada</Etiqueta>;
  if (a.status === 'fechada') return <Etiqueta tom="ok">Fechada</Etiqueta>;
  if (a.status === 'reaberta') return <Etiqueta tom="aviso">Reaberta</Etiqueta>;
  if (a.started_at) return <Etiqueta tom="aviso">Em coleta</Etiqueta>;
  return <Etiqueta>A coletar</Etiqueta>;
}

/** Detalhe da visita: os animais planejados, e daqui abre a coleta de cada ficha. */
export function PaginaVisita() {
  const { id = '' } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const navegar = useNavigate();
  const [adicionando, setAdicionando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const consulta = useConsulta(
    async (f) => {
      const [visita, haras, animais, avaliacoes] = await Promise.all([
        f.cadastro ? f.cadastro.visita(id) : Promise.resolve(null),
        f.haras(),
        f.listarAnimais(),
        f.avaliacoes(),
      ]);
      return { visita, haras, animais, avaliacoes: avaliacoes.filter((a) => a.visit_id === id) };
    },
    [id],
  );

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {({ visita, haras, animais, avaliacoes }) => {
        if (!visita) return <EstadoVazio titulo="Visita não encontrada" acao={<BotaoLink to="/visitas">Voltar às visitas</BotaoLink>} />;
        const situacao = situacaoDaVisita(avaliacoes, visita);
        const proxima = proximaFicha(avaliacoes, animais);
        const fechadas = avaliacoes.filter((a) => a.status === 'fechada' || a.status === 'validada');
        /*
          Criterio de aceite da ficha completa: tempo por animal no app <= tempo no
          papel. A media sai daqui, das fichas fechadas nesta visita.
        */
        const tempoMedio = (nivel: 1 | 2) => {
          const tempos = fechadas
            .filter((a) => (nivel === 1 ? a.protocol_level === 1 : a.protocol_level >= 2))
            .map((a) => minutosDaFicha(a))
            .filter((m): m is number => m != null);
          return tempos.length ? `${Math.round(tempos.reduce((x, y) => x + y, 0) / tempos.length)} min` : '—';
        };
        const planejados = new Set(avaliacoes.map((a) => a.animal_id));
        const linhas = avaliacoes
          .map((a) => ({ avaliacao: a, animal: animais.find((x) => x.id === a.animal_id) }))
          .filter((l) => l.animal)
          .sort((x, y) => x.animal!.name.localeCompare(y.animal!.name, 'pt-BR'));

        const retirar = async (avaliacaoId: string) => {
          setErro(null);
          try {
            await fonte.cadastro!.retirarDaVisita(avaliacaoId);
          } catch (e) {
            setErro(e instanceof Error ? e.message : String(e));
          }
        };

        return (
          <div className="space-y-6">
            <CabecalhoPagina
              trilha={[{ rotulo: 'Visitas', para: '/visitas' }, { rotulo: `Visita nº ${visita.visit_number}` }]}
              sobretitulo={haras?.name}
              titulo={`Visita nº ${visita.visit_number}`}
              etiquetas={
                <>
                  <Etiqueta tom={situacao === 'planejada' ? 'destaque' : situacao === 'fechada' ? 'ok' : 'aviso'}>
                    {ROTULO_SITUACAO[situacao]}
                  </Etiqueta>
                  {visita.purpose === 'estudo_repetibilidade' && <Etiqueta tom="marca">Estudo de repetibilidade</Etiqueta>}
                  <NumeroProvisorio visitaId={visita.id} />
                </>
              }
              meta={<span className="numero">{formatarData(visita.visit_date)} · protocolo v1.0</span>}
              acoes={
                <>
                {!visita.closed_at && <BotaoLink to={`/visitas/${visita.id}/editar`}>Editar</BotaoLink>}
                <Botao
                  variante="principal"
                  disabled={!proxima}
                  title={proxima ? undefined : 'Nenhuma ficha a coletar'}
                  onClick={() => proxima && navegar(`/visitas/${visita.id}/fichas/${proxima.id}`)}
                >
                  {situacao === 'planejada' ? 'Iniciar coleta' : 'Continuar coleta'}
                </Botao>
                </>
              }
            />

            <section className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda sm:grid-cols-4">
              <Indicador rotulo="Fichas fechadas" valor={`${fechadas.length}/${avaliacoes.length}`} />
              <Indicador rotulo="Completos" valor={avaliacoes.filter((a) => a.protocol_level >= 2).length} complemento="nível 2" />
              <Indicador rotulo="Tempo médio · completa" valor={tempoMedio(2)} complemento="por ficha fechada" />
              <Indicador rotulo="Tempo médio · censo" valor={tempoMedio(1)} complemento="por ficha fechada" />
            </section>

            {erro && (
              <p role="alert" className="border-l-2 border-erro bg-erro/8 px-3 py-2 text-sm text-erro">
                {erro}
              </p>
            )}

            <Painel
              titulo="Animais planejados"
              corpo="p-0"
              acoes={
                situacao === 'planejada' &&
                !visita.closed_at &&
                !adicionando && (
                  <Botao tamanho="sm" onClick={() => setAdicionando(true)}>
                    Incluir animal
                  </Botao>
                )
              }
            >
              {adicionando && (
                <div className="space-y-2 border-b border-borda bg-superficie-2 p-4">
                  <BuscaAnimal
                    rotulo="Animal a incluir"
                    animais={animais}
                    valor={null}
                    apenas={(a) => a.owner_farm_id === visita.stud_farm_id && !planejados.has(a.id)}
                    onEscolher={(a) => {
                      if (a) void fonte.cadastro!.incluirNaVisita(visita.id, a.id, 2);
                      setAdicionando(false);
                    }}
                  />
                  <Botao tamanho="sm" variante="discreto" onClick={() => setAdicionando(false)}>
                    Cancelar
                  </Botao>
                </div>
              )}
              {linhas.length === 0 ? (
                <p className="px-4 py-4 text-sm text-apagado">Nenhum animal nesta visita.</p>
              ) : (
                <Tabela rotulo="Animais planejados" empilhar>
                  <thead>
                    <tr>
                      <th scope="col" className={classeTh}>
                        Animal
                      </th>
                      <th scope="col" className={`${classeTh} hidden sm:table-cell`}>
                        Classe na visita
                      </th>
                      <th scope="col" className={`${classeTh} hidden text-right md:table-cell`}>
                        Idade na visita
                      </th>
                      <th scope="col" className={classeTh}>
                        Nível
                      </th>
                      <th scope="col" className={classeTh}>
                        Ficha
                      </th>
                      <th scope="col" className={classeTh}>
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map(({ avaliacao, animal }) => (
                      <tr key={avaliacao.id}>
                        <td className={classeTd}>
                          <Link to={`/animais/${animal!.id}`} className="font-serif font-semibold text-destaque-texto hover:underline">
                            {animal!.name}
                          </Link>
                        </td>
                        <td data-rotulo="" className={`${classeTd} hidden sm:table-cell`}>
                          <Etiqueta>{ROTULO_CLASSE[classeNaData(animal!, animais, visita.visit_date)]}</Etiqueta>
                        </td>
                        <td data-rotulo="" className={`${classeTd} numero hidden text-right md:table-cell`}>
                          {idadeDoAnimal(animal!, visita.visit_date)}
                        </td>
                        <td data-rotulo="" className={classeTd}>{avaliacao.protocol_level >= 2 ? 'Completo' : 'Censo'}</td>
                        <td data-rotulo="" className={classeTd}>
                          <span className="flex flex-wrap gap-1">
                            <EtiquetaDaFicha a={avaliacao} />
                            {avaliacao.sampled_for_double_measure && <Etiqueta tom="destaque">Dupla medição</Etiqueta>}
                          </span>
                        </td>
                        <td data-acao className={`${classeTd} whitespace-nowrap text-right`}>
                          <BotaoLink tamanho="sm" variante="discreto" to={`/visitas/${visita.id}/fichas/${avaliacao.id}`}>
                            {avaliacao.status === 'rascunho' || avaliacao.status === 'reaberta' ? 'Coletar' : 'Ver'}
                          </BotaoLink>
                          {avaliacao.status === 'rascunho' && !avaliacao.started_at && (
                            <Botao tamanho="sm" variante="discreto" onClick={() => void retirar(avaliacao.id)}>
                              Retirar
                            </Botao>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Tabela>
              )}
            </Painel>

            <FechamentoDoDia visitaId={visita.id} encerrada={visita.closed_at} avaliacoes={avaliacoes} animais={animais} />
          </div>
        );
      }}
    </Aguardar>
  );
}

/**
 * Fechamento do dia (folha 1 da ficha): antes de sair do haras, a conferencia
 * da visita inteira. Encerrar exige toda ficha fechada — e cada ficha so fecha
 * sem erro, inclusive a sorteada sem a segunda medicao. O que ainda nao subiu
 * nao bloqueia (no haras pode nao haver rede), mas fica dito em voz alta.
 */
function FechamentoDoDia({
  visitaId,
  encerrada,
  avaliacoes,
  animais,
}: {
  visitaId: string;
  encerrada: string | null;
  avaliacoes: readonly Avaliacao[];
  animais: readonly { id: string; name: string }[];
}) {
  const cadastro = useFonte().cadastro!;
  const porEnviar = useLiveQuery(() => db.outbox.count(), [], 0);
  const arquivosPorEnviar = useLiveQuery(async () => (await arquivos.pendentes()).length, [], 0);
  const [erro, setErro] = useState<string | null>(null);
  const [encerrando, setEncerrando] = useState(false);

  const nome = (id: string) => animais.find((a) => a.id === id)?.name ?? '—';
  const abertas = avaliacoes.filter((a) => a.status !== 'fechada' && a.status !== 'validada');
  const sorteadas = avaliacoes.filter((a) => a.sampled_for_double_measure);

  if (encerrada) {
    return (
      <Painel titulo="Fechamento do dia">
        <p className="text-sm">
          Visita encerrada em <span className="numero">{new Date(encerrada).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</span>.
        </p>
      </Painel>
    );
  }

  const encerrar = async () => {
    setErro(null);
    setEncerrando(true);
    try {
      await cadastro.fecharVisita(visitaId);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEncerrando(false);
    }
  };

  return (
    <Painel titulo="Fechamento do dia">
      <ul className="space-y-2 text-sm">
        <li className={abertas.length ? 'text-aviso' : 'text-ok'}>
          {abertas.length
            ? `Fichas ainda abertas (${abertas.length}): ${abertas.map((a) => nome(a.animal_id)).join(', ')}.`
            : 'Todas as fichas estão fechadas.'}
        </li>
        <li className="text-apagado">
          Dupla medição: {sorteadas.length} ficha(s) sorteada(s)
          {sorteadas.length ? ` — ${sorteadas.map((a) => nome(a.animal_id)).join(', ')}` : ' (o sorteio acontece quando a primeira ficha começa)'}.
        </li>
        <li className={porEnviar + arquivosPorEnviar ? 'text-aviso' : 'text-apagado'}>
          {porEnviar + arquivosPorEnviar
            ? `Ainda não enviados ao servidor: ${porEnviar} registro(s) e ${arquivosPorEnviar} foto(s)/vídeo(s). Sincronize antes de sair do haras, se houver internet.`
            : 'Tudo já foi enviado ao servidor.'}
        </li>
      </ul>
      {erro && <p className="mt-3 text-sm text-erro">{erro}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Botao variante="principal" disabled={abertas.length > 0 || avaliacoes.length === 0} carregando={encerrando} onClick={() => void encerrar()}>
          Encerrar a visita
        </Botao>
        {abertas.length > 0 && <span className="text-xs text-apagado">Feche ou retire as fichas abertas para encerrar.</span>}
      </div>
    </Painel>
  );
}

/**
 * O numero da visita e provisorio enquanto ela nao sobe: outro tablet, offline,
 * pode ter usado o mesmo. Se isso acontecer, a sincronizacao oferece renumerar.
 */
function NumeroProvisorio({ visitaId }: { visitaId: string }) {
  const pendente = useLiveQuery(
    () => db.outbox.where('entity').equals('visit').filter((i) => i.entityId === visitaId).count(),
    [visitaId],
    0,
  );
  return pendente > 0 ? <Etiqueta>Nº provisório: ainda não enviado</Etiqueta> : null;
}
